import { useState } from 'react';

import { Notice } from '@/components/Modal';
import { safeHref } from '@/lib/utils/url';
import {
  uploadCareerEvidence,
  useMyCareerStatements,
  useSubmitCareerStatement,
  useWithdrawCareerStatement,
  type CareerStatement,
} from '@/pages/mypage/useMyPageExtras';

/**
 * 경력기술 — 본인이 적고, 인사팀이 증명서를 보고 확정한다 (V321 · 사용자 지시 2026-09-22:
 * 「본인 경력기술을 직접 입력할 수 있게 하자. 대신에 경력기술증명서를 반드시 첨부해야하고,
 * 첨부 시 인사팀이 그걸 확인해서 승인 하면, 확정되도록」).
 *
 * <h2>증명서는 <b>반드시</b> 붙는다</h2>
 * 파일을 고르기 전에는 제출 버튼이 눌리지 않는다 — 그러나 버튼을 <b>감추지는 않는다</b>.
 * 누르면 왜 안 되는지가 그 자리에서 보여야 한다(서버도 같은 것을 거절한다).
 *
 * <h2>반려된 것은 고치는 것이 아니라 다시 낸다</h2>
 * 낸 글을 고칠 수 있게 하면 인사팀이 본 문장과 지금 화면의 문장이 달라진다. 반려 사유가 달린 줄은
 * 그대로 남고, 새로 내면 새 줄이 선다 — 무엇을 고쳐 다시 냈는지가 이력으로 남는다.
 */
export function CareerStatementSection() {
  const { data, isPending, error } = useMyCareerStatements();
  const [writing, setWriting] = useState(false);

  return (
    <section>
      <div className="flex items-center gap-2">
        <h3 style={sectionTitle}>경력기술</h3>
        <button className="btn-secondary ml-auto" style={compactButton} onClick={() => setWriting((open) => !open)}>
          {writing ? '접기' : '경력기술 등록'}
        </button>
      </div>

      {writing && <StatementForm onDone={() => setWriting(false)} />}

      {isPending ? (
        <p style={mutedText}>불러오는 중입니다.</p>
      ) : error ? (
        <Notice tone="error">경력기술을 불러오지 못했습니다.</Notice>
      ) : (data ?? []).length === 0 ? (
        <p style={mutedText}>등록한 경력기술이 없습니다.</p>
      ) : (
        (data ?? []).map((row) => <StatementRow key={row.id} row={row} />)
      )}
    </section>
  );
}

const STATUS_BADGE: Record<CareerStatement['status'], { className: string; label: string }> = {
  PENDING: { className: 'badge badge-yellow', label: '확인 중' },
  APPROVED: { className: 'badge badge-green', label: '확정' },
  REJECTED: { className: 'badge badge-gray', label: '반려' },
};

function StatementRow({ row }: { row: CareerStatement }) {
  const withdraw = useWithdrawCareerStatement();
  const badge = STATUS_BADGE[row.status];

  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={badge.className}>{badge.label}</span>
        <span style={mutedText}>
          {row.periodFrom ?? ''}
          {row.periodFrom || row.periodTo ? ' ~ ' : ''}
          {row.periodTo ?? ''}
        </span>
        {row.evidenceUrl && (
          <a href={safeHref(row.evidenceUrl)} target="_blank" rel="noreferrer" className="badge badge-blue" style={{ fontSize: '11px' }}>
            {row.evidenceName ?? '증명서'}
          </a>
        )}
        {row.status === 'PENDING' && (
          <button
            className="ml-auto"
            style={{ ...mutedText, textDecoration: 'underline' }}
            disabled={withdraw.isPending}
            onClick={() => withdraw.mutate(row.id)}
          >
            취소
          </button>
        )}
      </div>
      {/* 본인이 적은 문장은 줄바꿈 그대로 보인다 — 현장을 한 줄씩 적는 사람이 많다. */}
      <p style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>{row.content}</p>
      {row.reviewNote && (
        <p style={{ ...mutedText, marginTop: '4px' }}>
          {row.status === 'REJECTED' ? '반려 사유' : '확인 의견'} — {row.reviewNote}
          {row.reviewerName ? ` (${row.reviewerName})` : ''}
        </p>
      )}
      {withdraw.isError && <p style={errorText}>{withdraw.error.message}</p>}
    </div>
  );
}

function StatementForm({ onDone }: { onDone: () => void }) {
  const submit = useSubmitCareerStatement();
  const [content, setContent] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setError(null);
    if (!content.trim()) {
      setError('경력기술 내용을 적어 주세요.');
      return;
    }
    if (!file) {
      setError('경력기술증명서를 첨부해 주세요.');
      return;
    }

    setBusy(true);
    try {
      // 파일을 먼저 올리고 키만 싣는다. 실패하면 줄이 서기 전에 멈춘다 — 증명서 없는 신고가
      // 대기 목록에 쌓이지 않는다.
      const evidenceKey = await uploadCareerEvidence(file);
      await submit.mutateAsync({
        content: content.trim(),
        periodFrom: periodFrom || null,
        periodTo: periodTo || null,
        evidenceKey,
        evidenceName: file.name,
      });
      onDone();
    }
    catch (cause) {
      setError(cause instanceof Error ? cause.message : '제출하지 못했습니다.');
    }
    finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg space-y-2" style={{ border: '1px solid var(--border)', padding: '10px 12px', margin: '6px 0' }}>
      <textarea
        className="input-field"
        rows={5}
        placeholder="담당한 현장·사업과 맡은 일을 적어 주세요."
        value={content}
        onChange={(event) => setContent(event.target.value)}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <input
          className="input-field"
          type="date"
          aria-label="시작일"
          style={{ width: '150px' }}
          value={periodFrom}
          onChange={(event) => setPeriodFrom(event.target.value)}
        />
        <span style={mutedText}>~</span>
        <input
          className="input-field"
          type="date"
          aria-label="종료일"
          style={{ width: '150px' }}
          value={periodTo}
          onChange={(event) => setPeriodTo(event.target.value)}
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="btn-secondary" style={{ ...compactButton, cursor: busy ? 'default' : 'pointer' }}>
          경력기술증명서 첨부
          <input
            type="file"
            hidden
            disabled={busy}
            onChange={(event) => {
              const picked = event.target.files?.[0] ?? null;
              event.target.value = '';
              setFile(picked);
            }}
          />
        </label>
        <span style={mutedText}>{file ? file.name : '첨부해야 제출할 수 있습니다.'}</span>
        <button className="btn-primary ml-auto" style={compactButton} disabled={busy} onClick={() => void send()}>
          {busy ? '제출 중...' : '제출'}
        </button>
      </div>

      {error && <p style={errorText}>{error}</p>}
    </div>
  );
}

const compactButton = { fontSize: '12px', padding: '5px 12px' } as const;
const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
const errorText = { fontSize: '12px', color: 'var(--danger)' } as const;
const sectionTitle = { fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' } as const;
